import {
  DISTANCE_UNITS,
  ENTITY_ROLES,
  HORSE_BREEDS,
  HORSE_COLORS,
  HORSE_INACTIVE_REASONS,
  HORSE_LIFECYCLE_STATUSES,
  HORSE_RECORD_STATES,
  HORSE_SCAN_FIELD_NAMES,
  HORSE_SCAN_KINDS,
  HORSE_SEXES,
  MASS_UNITS,
  STABLE_FEATURES,
  TEMPERATURE_UNITS,
  VACCINATION_SCAN_FIELD_NAMES,
} from "@hisani/stable";
import { z } from "zod";

const EntityRoleSchema = z.enum(ENTITY_ROLES);
const StableFeatureSchema = z.enum(STABLE_FEATURES);
const FeatureAccessLevelSchema = z.enum(["FULL", "VIEW", "NONE"]);

export const StableSummarySchema = z
  .object({
    id: z.string(),
    publicId: z.string(),
    name: z.string(),
    countryCode: z.string().nullable(),
    subscriptionTier: z.enum(["FREE", "PAID"]),
    entityOwnerHorsemanId: z.string(),
    // Optional (not required) so an older cached response or a hand-built
    // test fixture predating Accounting still parses under `.strict()` —
    // only the key's *presence* needs declaring here for the real API's
    // now-always-present field to be accepted, not its mandatory presence.
    accountingAutoInvoiceEnabled: z.boolean().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .strict();

const StableMembershipSummarySchema = z
  .object({
    stable: StableSummarySchema,
    entityRole: EntityRoleSchema,
    isEntityOwner: z.boolean(),
  })
  .strict();

export const StableListSchema = z.object({
  stables: z.array(StableMembershipSummarySchema),
});

const VaccineTypeConfigSchema = z
  .object({
    code: z.string(),
    label: z.string(),
    intervalDays: z.number().int().nullable(),
    alertingEnabled: z.boolean(),
  })
  .strict();

const ExpenseCategoryConfigSchema = z
  .object({ code: z.string(), label: z.string() })
  .strict();

export const StableDashboardSchema = z
  .object({
    stable: StableSummarySchema,
    actorEntityRole: EntityRoleSchema,
    isEntityOwner: z.boolean(),
    effectivePermissions: z.record(
      StableFeatureSchema,
      FeatureAccessLevelSchema,
    ),
    activeHorseCount: z.number().int(),
    horseCap: z.number().int().nullable(),
    staffCount: z.number().int(),
    vaccineTypes: z.record(z.string(), VaccineTypeConfigSchema),
    // Optional for the same reason as `StableSummarySchema`'s
    // `accountingAutoInvoiceEnabled` above — declares the key so the real
    // API's always-present field is accepted under `.strict()` without
    // forcing every pre-existing dashboard fixture to be updated.
    expenseCategories: z
      .record(z.string(), ExpenseCategoryConfigSchema)
      .optional(),
  })
  .strict();

export const StaffMemberSchema = z
  .object({
    membershipId: z.string(),
    horsemanId: z.string(),
    displayName: z.string().nullable(),
    accountKind: z.enum(["REGISTERED", "SHADOW"]),
    entityRole: EntityRoleSchema,
    overrides: z.record(StableFeatureSchema, FeatureAccessLevelSchema),
    invitePending: z.boolean(),
    joinedAt: z.coerce.date(),
  })
  .strict();

export const StaffListSchema = z.object({ staff: z.array(StaffMemberSchema) });

export const HorseDetailsSchema = z
  .object({
    id: z.string(),
    stableId: z.string(),
    name: z.string(),
    breed: z.enum(HORSE_BREEDS),
    breedOtherLabel: z.string().nullable(),
    sex: z.enum(HORSE_SEXES),
    geldedOn: z.string().nullable(),
    dateOfBirth: z.object({
      year: z.number().int(),
      month: z.number().int().nullable(),
      day: z.number().int().nullable(),
    }),
    racingAge: z.number().int(),
    passportNumber: z.string().nullable(),
    sire: z.string().nullable(),
    dam: z.string().nullable(),
    bredIn: z.string().nullable(),
    color: z.enum(HORSE_COLORS).nullable(),
    barnNumber: z.string().nullable(),
    hemisphereOverride: z.enum(["NORTHERN", "SOUTHERN"]).nullable(),
    lifecycleStatus: z.enum(HORSE_LIFECYCLE_STATUSES),
    recordState: z.enum(HORSE_RECORD_STATES),
    recordInactiveReason: z.enum(HORSE_INACTIVE_REASONS).nullable(),
    recordInactiveUntil: z.coerce.date().nullable(),
    version: z.number().int(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .strict();

export const HorseListSchema = z.object({
  horses: z.array(HorseDetailsSchema),
});

// --- Horse-add AI scan (passport/name-plate photo -> confirm-screen draft) ---

const HorseScanFieldSchema = z
  .object({ value: z.string().nullable(), confidence: z.number().nullable() })
  .strict();

export const HorseScanDraftSchema = z
  .object({
    extractionId: z.string(),
    kind: z.enum(HORSE_SCAN_KINDS),
    fields: z.record(z.enum(HORSE_SCAN_FIELD_NAMES), HorseScanFieldSchema),
    missingCompulsoryFields: z.array(z.enum(HORSE_SCAN_FIELD_NAMES)),
    provider: z.string(),
    modelId: z.string(),
    modelVersion: z.string().nullable(),
    extractedAt: z.coerce.date(),
  })
  .strict();
export type HorseScanDraft = z.infer<typeof HorseScanDraftSchema>;

export const HorseScanUploadDescriptorSchema = z
  .object({
    id: z.string(),
    objectKey: z.string(),
    uploadUrl: z.string().url(),
  })
  .strict();

export const AddHorseResultSchema = z.discriminatedUnion("status", [
  z
    .object({ status: z.literal("CREATED"), horse: HorseDetailsSchema })
    .strict(),
  z
    .object({
      status: z.literal("DUPLICATE_WARNING"),
      warningType: z.literal("PASSPORT_MATCH"),
    })
    .strict(),
]);

// --- Temperature/Weight/Feed Records (Sealed, append-only) ---

const MassSchema = z
  .object({ value: z.number(), unit: z.enum(MASS_UNITS) })
  .strict();

const TemperatureValueSchema = z
  .object({ value: z.number(), unit: z.enum(TEMPERATURE_UNITS) })
  .strict();

const careRecordCommonSchema = z.object({
  id: z.string(),
  stableId: z.string(),
  horseId: z.string(),
  eventTime: z.coerce.date(),
  createdByHorsemanId: z.string(),
  createdAt: z.coerce.date(),
});

const WeightRecordSchema = careRecordCommonSchema
  .extend({ weight: MassSchema })
  .strict();

const TemperatureRecordSchema = careRecordCommonSchema
  .extend({ temperature: TemperatureValueSchema })
  .strict();

const FeedRecordSchema = careRecordCommonSchema
  .extend({ description: z.string(), amount: MassSchema.nullable() })
  .strict();

const paginationCursorSchema = z
  .object({ at: z.coerce.date(), id: z.string() })
  .strict();

function carePageSchema<T extends z.ZodTypeAny>(record: T) {
  return z
    .object({
      records: z.array(record),
      nextCursor: paginationCursorSchema.nullable(),
    })
    .strict();
}

export const WeightRecordPageSchema = carePageSchema(WeightRecordSchema);
export const TemperatureRecordPageSchema = carePageSchema(
  TemperatureRecordSchema,
);
export const FeedRecordPageSchema = carePageSchema(FeedRecordSchema);

// --- Training Scheduler ---

const LotSchema = z
  .object({
    id: z.string(),
    stableId: z.string(),
    name: z.string(),
    sortOrder: z.number().int(),
    createdAt: z.coerce.date(),
  })
  .strict();

export const LotListSchema = z.object({ lots: z.array(LotSchema) });

const TrainingDistanceOrDurationSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("DISTANCE"),
      distance: z
        .object({ value: z.number(), unit: z.enum(DISTANCE_UNITS) })
        .strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("DURATION"),
      durationMilliseconds: z.number(),
    })
    .strict(),
]);

export const TrainingEntrySchema = z
  .object({
    id: z.string(),
    stableId: z.string(),
    horseId: z.string(),
    day: z.string(),
    workType: z.string(),
    distanceOrDuration: TrainingDistanceOrDurationSchema,
    instructions: z.string().nullable(),
    lotId: z.string().nullable(),
    riderHorsemanId: z.string().nullable(),
    outcomeNotes: z.string().nullable(),
    loggedByHorsemanId: z.string().nullable(),
    loggedAt: z.coerce.date().nullable(),
    version: z.number().int(),
    createdByHorsemanId: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .strict();

const TrainingBoardRiderSchema = z
  .object({ horsemanId: z.string(), displayName: z.string().nullable() })
  .strict();

export const TrainingBoardSchema = z
  .object({
    day: z.string(),
    lots: z.array(LotSchema),
    riders: z.array(TrainingBoardRiderSchema),
    entries: z.array(TrainingEntrySchema),
  })
  .strict();

export const TrainingEntryPrefillSchema = z
  .object({
    workType: z.string().nullable(),
    distanceOrDuration: TrainingDistanceOrDurationSchema.nullable(),
    instructions: z.string().nullable(),
  })
  .strict();

const BulkAssignResultSchema = z.discriminatedUnion("status", [
  z
    .object({
      horseId: z.string(),
      status: z.literal("CREATED"),
      entry: TrainingEntrySchema,
    })
    .strict(),
  z
    .object({ horseId: z.string(), status: z.literal("ALREADY_SCHEDULED") })
    .strict(),
  z
    .object({ horseId: z.string(), status: z.literal("NOT_ACCEPTING_RECORDS") })
    .strict(),
]);

export const BulkAssignResultListSchema = z.object({
  results: z.array(BulkAssignResultSchema),
});

const StopwatchSectionalSchema = z
  .object({
    sequence: z.number().int(),
    distanceMeters: z.number(),
    cumulativeMilliseconds: z.number().int(),
    splitMilliseconds: z.number().int(),
  })
  .strict();

const StopwatchSessionSchema = z
  .object({
    id: z.string(),
    stableId: z.string(),
    horseId: z.string(),
    createdByHorsemanId: z.string(),
    label: z.string().nullable(),
    elapsedMilliseconds: z.number().int(),
    sectionals: z.array(StopwatchSectionalSchema),
    occurredAt: z.coerce.date(),
    createdAt: z.coerce.date(),
  })
  .strict();

const TrainingTimelineEntrySchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("TRAINING_ENTRY"),
      at: z.coerce.date(),
      entry: TrainingEntrySchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal("STOPWATCH_SESSION"),
      at: z.coerce.date(),
      session: StopwatchSessionSchema,
    })
    .strict(),
]);

export const TrainingTimelinePageSchema = carePageSchema(
  TrainingTimelineEntrySchema,
);

// --- Vet Records / Farrier Records ---

const SealedRecordDocumentSchema = z
  .object({
    id: z.string(),
    filename: z.string(),
    contentType: z.string(),
    size: z.number(),
    uploadedByHorsemanId: z.string(),
    uploadedAt: z.coerce.date(),
  })
  .strict();

/** Vet Records and Farrier Records share one structural shape (a scheduled/
 * upcoming item or a directly logged completed visit, either later
 * transitioning to completed in place) — see `SealedRecordEntry` in
 * `@hisani/stable`. */
const SealedRecordEntrySchema = z
  .object({
    id: z.string(),
    stableId: z.string(),
    horseId: z.string(),
    dueDate: z.string().nullable(),
    scheduledNotes: z.string().nullable(),
    outcomeNotes: z.string().nullable(),
    loggedByHorsemanId: z.string().nullable(),
    loggedAt: z.coerce.date().nullable(),
    documents: z.array(SealedRecordDocumentSchema),
    version: z.number().int(),
    createdByHorsemanId: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .strict();

export const VetRecordSchema = SealedRecordEntrySchema;
export const FarrierRecordSchema = SealedRecordEntrySchema;
export const VetRecordPageSchema = carePageSchema(VetRecordSchema);
export const FarrierRecordPageSchema = carePageSchema(FarrierRecordSchema);

export const SealedRecordUploadDescriptorSchema = z
  .object({
    id: z.string(),
    objectKey: z.string(),
    uploadUrl: z.string().url(),
  })
  .strict();

// --- Vaccinations ---

export const VaccinationRecordSchema = z
  .object({
    id: z.string(),
    stableId: z.string(),
    horseId: z.string(),
    vaccineType: z.string(),
    dueDate: z.string().nullable(),
    scheduledNotes: z.string().nullable(),
    dateAdministered: z.string().nullable(),
    batchSerial: z.string().nullable(),
    outcomeNotes: z.string().nullable(),
    nextDue: z.string().nullable(),
    nextDueOverridden: z.boolean(),
    loggedByHorsemanId: z.string().nullable(),
    loggedAt: z.coerce.date().nullable(),
    documents: z.array(SealedRecordDocumentSchema),
    version: z.number().int(),
    createdByHorsemanId: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .strict();

export const VaccinationRecordPageSchema = carePageSchema(
  VaccinationRecordSchema,
);

const VaccinationScanFieldSchema = z
  .object({ value: z.string().nullable(), confidence: z.number().nullable() })
  .strict();

export const VaccinationScanDraftSchema = z
  .object({
    extractionId: z.string(),
    fields: z.record(
      z.enum(VACCINATION_SCAN_FIELD_NAMES),
      VaccinationScanFieldSchema,
    ),
    missingCompulsoryFields: z.array(z.enum(VACCINATION_SCAN_FIELD_NAMES)),
    provider: z.string(),
    modelId: z.string(),
    modelVersion: z.string().nullable(),
    extractedAt: z.coerce.date(),
  })
  .strict();
export type VaccinationScanDraft = z.infer<typeof VaccinationScanDraftSchema>;

export const VaccinationScanUploadDescriptorSchema = z
  .object({
    id: z.string(),
    objectKey: z.string(),
    uploadUrl: z.string().url(),
  })
  .strict();

const HorseVaccinationAlertSchema = z
  .object({
    horseId: z.string(),
    vaccineType: z.string(),
    vaccineTypeLabel: z.string(),
    nextDue: z.string(),
    severity: z.enum(["WARNING", "NUDGE", "OVERDUE"]),
  })
  .strict();
export type HorseVaccinationAlert = z.infer<typeof HorseVaccinationAlertSchema>;

export const StableVaccinationAlertsSchema = z.array(
  HorseVaccinationAlertSchema,
);

// --- Accounting ---

const BillHorseAllocationSchema = z
  .object({
    horseId: z.string(),
    basisPoints: z.number().int(),
    amountMinor: z.string(),
  })
  .strict();

const BillSchema = z
  .object({
    id: z.string(),
    stableId: z.string(),
    vendor: z.string(),
    billDate: z.string(),
    description: z.string().nullable(),
    category: z.string(),
    amountMinor: z.string(),
    currency: z.string(),
    allocationMode: z.enum(["HORSE_LINK", "PERCENTAGE", "UNLINKED"]),
    allocations: z.array(BillHorseAllocationSchema),
    documents: z.array(SealedRecordDocumentSchema),
    version: z.number().int(),
    createdByHorsemanId: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .strict();
export type Bill = z.infer<typeof BillSchema>;

export const BillPageSchema = carePageSchema(BillSchema);

export const BillUploadDescriptorSchema = z
  .object({
    id: z.string(),
    objectKey: z.string(),
    uploadUrl: z.string().url(),
  })
  .strict();

const BillScanFieldSchema = z
  .object({ value: z.string().nullable(), confidence: z.number().nullable() })
  .strict();

const BILL_SCAN_FIELD_NAME_VALUES = [
  "vendor",
  "billDate",
  "description",
  "category",
  "currency",
  "amountMinor",
] as const;

export const BillScanDraftSchema = z
  .object({
    extractionId: z.string(),
    fields: z.record(z.enum(BILL_SCAN_FIELD_NAME_VALUES), BillScanFieldSchema),
    missingCompulsoryFields: z.array(z.enum(BILL_SCAN_FIELD_NAME_VALUES)),
    provider: z.string(),
    modelId: z.string(),
    modelVersion: z.string().nullable(),
    extractedAt: z.coerce.date(),
  })
  .strict();
export type BillScanDraft = z.infer<typeof BillScanDraftSchema>;

const InvoiceLineItemSchema = z
  .object({
    id: z.string(),
    billId: z.string(),
    horseId: z.string(),
    horseName: z.string(),
    categoryCode: z.string(),
    categoryLabel: z.string(),
    vendor: z.string(),
    billDate: z.string(),
    description: z.string().nullable(),
    amountMinor: z.string(),
  })
  .strict();

const InvoiceSnapshotLineItemSchema = z
  .object({
    billId: z.string(),
    horseId: z.string(),
    horseName: z.string(),
    categoryCode: z.string(),
    categoryLabel: z.string(),
    vendor: z.string(),
    billDate: z.string(),
    description: z.string().nullable(),
    amountMinor: z.string(),
  })
  .strict();

/** The frozen payload written once at invoice approval
 * (`docs/products/stable.md`: "Approving an invoice creates an immutable
 * snapshot"). Present only on an `APPROVED` invoice. */
const InvoiceSnapshotPayloadSchema = z
  .object({
    invoiceId: z.string(),
    stableId: z.string(),
    stableName: z.string(),
    horseId: z.string().nullable(),
    ownerHorsemanId: z.string(),
    dateRangeStart: z.string(),
    dateRangeEnd: z.string(),
    currency: z.string(),
    lineItems: z.array(InvoiceSnapshotLineItemSchema),
    totalMinor: z.string(),
    generatedAt: z.string(),
    approvedAt: z.string(),
    approvedByHorsemanId: z.string(),
  })
  .strict();

const InvoiceSchema = z
  .object({
    id: z.string(),
    stableId: z.string(),
    horseId: z.string().nullable(),
    ownerHorsemanId: z.string(),
    dateRangeStart: z.string(),
    dateRangeEnd: z.string(),
    currency: z.string(),
    status: z.enum(["DRAFT", "APPROVED"]),
    lineItems: z.array(InvoiceLineItemSchema),
    totalMinor: z.string(),
    version: z.number().int(),
    generatedByHorsemanId: z.string().nullable(),
    approvedByHorsemanId: z.string().nullable(),
    approvedAt: z.coerce.date().nullable(),
    lastSharedAt: z.coerce.date().nullable(),
    snapshot: InvoiceSnapshotPayloadSchema.nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .strict();
export type Invoice = z.infer<typeof InvoiceSchema>;

export const InvoicePageSchema = carePageSchema(InvoiceSchema);

/** The well-defined outcome of generating an invoice draft — a real `DRAFT`
 * (even one with zero line items), or the honest "no Primary Owner linked
 * yet" block every horse hits until Link An Owner ships
 * (`domain/accounting/owner-link.ts`), never a crash or silent no-op. */
export const InvoiceGenerationResultSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("DRAFT"), invoice: InvoiceSchema }).strict(),
  z.object({ status: z.literal("NO_PRIMARY_OWNER") }).strict(),
  z.object({ status: z.literal("NO_LINKED_HORSES") }).strict(),
]);

// --- Link An Owner ---

const HorsemanSearchResultSchema = z
  .object({
    id: z.string(),
    displayName: z.string().nullable(),
    primaryRole: z.string().nullable(),
  })
  .strict();

export const HorsemanSearchResultsSchema = z.object({
  results: z.array(HorsemanSearchResultSchema),
});

const OWNER_LINK_REQUEST_STATUSES = [
  "PENDING",
  "ACCEPTED",
  "DECLINED",
  "CANCELLED",
  "EXPIRED",
] as const;

export const OwnerLinkRequestSchema = z
  .object({
    id: z.string(),
    stableId: z.string(),
    horseId: z.string(),
    recipient: z.discriminatedUnion("kind", [
      z
        .object({
          kind: z.literal("HORSEMAN"),
          displayName: z.string().nullable(),
        })
        .strict(),
      z
        .object({
          kind: z.literal("INVITED_RECIPIENT"),
          contactPointType: z.enum(["EMAIL", "PHONE"]),
        })
        .strict(),
    ]),
    requestedByHorsemanId: z.string(),
    status: z.enum(OWNER_LINK_REQUEST_STATUSES),
    version: z.number().int().positive(),
    requestedAt: z.coerce.date(),
    respondedAt: z.coerce.date().nullable(),
    expiresAt: z.coerce.date(),
  })
  .strict();
export type OwnerLinkRequest = z.infer<typeof OwnerLinkRequestSchema>;

export const OwnerLinkRequestsSchema = z.object({
  requests: z.array(OwnerLinkRequestSchema),
});

export const HorseOwnerLinkSchema = z
  .object({
    id: z.string(),
    horseId: z.string(),
    horsemanId: z.string(),
    ownerDisplayName: z.string().nullable(),
    isPrimary: z.boolean(),
    linkedAt: z.coerce.date(),
    ownerLinksVersion: z.number().int().positive(),
  })
  .strict();
export type HorseOwnerLink = z.infer<typeof HorseOwnerLinkSchema>;
export const HorseOwnerLinksSchema = z.object({
  links: z.array(HorseOwnerLinkSchema),
});

const IncomingOwnerLinkRequestSchema = OwnerLinkRequestSchema.extend({
  horseName: z.string(),
  stableName: z.string(),
}).strict();

export const IncomingOwnerLinkRequestsSchema = z.object({
  requests: z.array(IncomingOwnerLinkRequestSchema),
});

export const RespondOwnerLinkRequestResultSchema = z.discriminatedUnion(
  "outcome",
  [
    z
      .object({
        outcome: z.literal("ACCEPTED"),
        request: OwnerLinkRequestSchema,
        link: HorseOwnerLinkSchema,
      })
      .strict(),
    z
      .object({
        outcome: z.literal("DECLINED"),
        request: OwnerLinkRequestSchema,
      })
      .strict(),
  ],
);

const AcceptedOwnerLinkForHorsemanSchema = z
  .object({
    horseId: z.string(),
    horseName: z.string(),
    stableId: z.string(),
    stableName: z.string(),
    isPrimary: z.boolean(),
    linkedAt: z.coerce.date(),
  })
  .strict();

export const AcceptedOwnerLinksForHorsemanSchema = z.object({
  links: z.array(AcceptedOwnerLinkForHorsemanSchema),
});

// --- Stable Transfer ---

const STABLE_TRANSFER_STATUSES = [
  "PENDING",
  "ACCEPTED",
  "DECLINED",
  "CANCELLED",
  "EXPIRED",
  "COMPLETED",
] as const;

export const StableTransferRequestSchema = z
  .object({
    id: z.string(),
    originStableId: z.string(),
    originStableName: z.string(),
    destinationStableId: z.string(),
    destinationStableName: z.string(),
    horseId: z.string(),
    horseName: z.string(),
    requestedByHorsemanId: z.string(),
    respondedByHorsemanId: z.string().nullable(),
    status: z.enum(STABLE_TRANSFER_STATUSES),
    ownerLinkHorsemanIdsToCarryForward: z.array(z.string()),
    requestedAt: z.coerce.date(),
    respondedAt: z.coerce.date().nullable(),
    completedAt: z.coerce.date().nullable(),
    expiresAt: z.coerce.date(),
    destinationHorseId: z.string().nullable(),
    version: z.number().int().positive(),
  })
  .strict();
export type StableTransferRequest = z.infer<typeof StableTransferRequestSchema>;

export const StableTransferRequestsSchema = z.object({
  requests: z.array(StableTransferRequestSchema),
});

export const CancelStableTransferResultSchema = z
  .object({
    cancelled: z.literal(true),
    request: StableTransferRequestSchema,
  })
  .strict();

export const StablePublicLookupResultSchema = z.discriminatedUnion("status", [
  z
    .object({
      status: z.literal("FOUND"),
      name: z.string(),
      activeHorseCount: z.number().int(),
    })
    .strict(),
  z.object({ status: z.literal("NOT_FOUND") }).strict(),
  z.object({ status: z.literal("RATE_LIMITED") }).strict(),
]);
